import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  DashboardLayout,
  MetricsCard,
  RecentBookingsTable,
} from "src/components/dashboard";
import user from "src/assets/images/metrics/user.svg";
import ads from "src/assets/images/sidebar/ads.png";
import artisan from "src/assets/images/metrics/artisan.svg";
import kyc from "src/assets/images/metrics/kyc.svg";
import booking from "src/assets/images/metrics/booking.svg";
import { Flex } from "src/components/ui";
import { theme } from "src/styles/Theme";
import { toast, ToastContainer } from "react-toastify";
import { ScaleLoader } from "react-spinners";
import { useLoading } from "src/hooks";
import { auth } from "src/store/Auth";
import ReferralTable from "src/components/dashboard/referralTable";
import {
  BookingMetricTypes,
  MetricTypes,
  ReferralListProps,
} from "./dashboardTypes";
import { PaginationProps } from "src/components/ui/Pagination";
import DashboardCharts from "src/components/dashboard/DashboardCharts";
import {
  useGetArtisanDashboardQuery,
  useGetUserDashboardQuery,
  useGetBookingDashboardQuery,
  useGetRecentDashboardBookingsQuery,
  useGetReferralsQuery,
} from "src/store/services/dashboardApi";
import { useNavigate } from "react-router-dom";

export const DashboardContainer = styled.div`
  .metrics__cards {
    margin: 2rem 0;
  }
  .loader-container {
    border-radius: 16px;
    background: #ffffff;
    height: 300px;
    margin-top: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const initialBookingState: BookingMetricTypes = {
  active_bookings: 0,
  canceled_bookings: 0,
  pending_bookings: 0,
  total_bookings: 0,
  completed_bookings: 0,
  bookings: [],
  ads_info: {
    totalAds: 0,
  },
};

const Dashboard = () => {
  const [metricData, setMetricData] = useState<MetricTypes>({
    bookingData: initialBookingState,
    userData: {
      pending_verification: 0,
      users: 0,
    },
    artisanData: {
      pending_verification: 0,
      artisans: 0,
      partners: 0,
    },
  });
  const { authUser } = auth.use();
  const navigate = useNavigate();
  
  if (authUser?.role?.includes("centralPartnerManager")) {
    navigate(`/partners/group/${authUser._id}`);
  }

  const [adminBookings, setAdminBookings] =
    useState<BookingMetricTypes>(initialBookingState);
  const [recentBookings, setRecentBookings] = useState<BookingsTypes[]>([]);
  const [allReferrals, setAllReferrals] = useState<ReferralListProps[]>([]);
  const { loading, startLoading, stopLoading } = useLoading();
  const [pageNumber, setPageNumber] = useState(1);
  const [bPageNumber, setBPageNumber] = useState(1);
  const [bPaginationData, setBPaginationData] = useState<PaginationProps>({
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  });

  const {
    loading: fetchRecentBookings,
    startLoading: startFetchingBookings,
    stopLoading: stopFetchingBookings,
  } = useLoading();

  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  // RTK queries
  const { data: artisanDash } = useGetArtisanDashboardQuery();
  const { data: userDash } = useGetUserDashboardQuery();
  const { data: bookingDash } = useGetBookingDashboardQuery();
  const [paginationData, setPaginationData] = useState<PaginationProps>({
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  });
  const [pageNo, setPageNo] = useState(1);
  const { data: recentDash, isLoading: isLoadingRecent } =
    useGetRecentDashboardBookingsQuery();
  const { data: referralsDash } = useGetReferralsQuery({
    page: pageNumber,
    limit: 10,
  });

  useEffect(() => {
    const artisanData = artisanDash?.payload?.data;
    if (artisanData)
      setMetricData((prev) => ({
        ...prev,
        artisanData,
      }));
  }, [artisanDash]);

  useEffect(() => {
    if (authUser?.role?.includes("occupationalManager")) return;
    const userData = userDash?.payload?.data;
    if (userData)
      setMetricData((prev) => ({
        ...prev,
        userData,
      }));
  }, [userDash, authUser]);

  useEffect(() => {
    if (authUser?.role?.includes("occupationalManager")) return;
    const bookingData = bookingDash?.payload?.data;
    if (bookingData)
      setMetricData((prev) => ({
        ...prev,
        bookingData,
      }));
  }, [bookingDash, authUser]);

  useEffect(() => {
    const bookings = recentDash?.payload?.data?.bookings;
    const pagination = recentDash?.payload?.pagination;
    if (bookings) setRecentBookings(bookings);
    if (pagination) setBPaginationData(pagination);
  }, [recentDash]);

  useEffect(() => {
    const rows = referralsDash?.payload?.data;
    const pagination = referralsDash?.payload?.pagination;
    if (rows) setAllReferrals(rows);
    if (pagination) setPaginationData(pagination);
  }, [referralsDash]);

  const occupationalManagerCard = [
    "Total Vendor",
    "Total Pending verification Vendor",
  ];

  const metrics = [
    {
      count: metricData?.userData?.users || 0,
      key: "Total Customers",
      img: user,
      color: theme.colors.purple,
      href: "/customers",
    },
    {
      count: metricData?.artisanData?.artisans || 0,
      key: "Total Vendor",
      img: artisan,
      color: theme.colors.blue,
      href: "/vendors",
    },
    {
      count: metricData?.artisanData?.partners || 0,
      key: "Total Outlets",
      img: artisan,
      color: theme.colors.blue,
      href: "/partners",
    },
    {
      count: metricData?.artisanData?.pending_verification || 0,
      key: "Total Pending verification Vendor",
      img: kyc,
      color: theme.colors.mustard,
      href: "/verification/vendor",
    },
    {
      count: metricData?.userData?.pending_verification || 0,
      key: "Total Pending verification Customer",
      img: kyc,
      color: theme.colors.mustard,
      href: "/verification/customer",
    },
    {
      count: metricData?.bookingData?.total_bookings || 0,
      key: "Total Booking ",
      img: booking,
      color: theme.colors.darkPurple,
      href: "/bookings?tabStatus=all",
    },
    {
      count: metricData?.bookingData?.active_bookings || 0,
      key: "Active Booking ",
      img: booking,
      color: theme.colors.purple,
      href: "/bookings?tabStatus=active",
    },
    {
      count: metricData?.bookingData?.pending_bookings || 0,
      key: "Pending Booking ",
      img: booking,
      color: theme.colors.cyan,
      href: "/bookings?tabStatus=pending",
    },
    {
      count: metricData?.bookingData?.canceled_bookings || 0,
      key: "Canceled Booking ",
      img: booking,
      color: theme.colors.red,
      href: "/bookings?tabStatus=canceled",
    },
    {
      count: metricData?.bookingData?.completed_bookings || 0,
      key: "Completed Booking ",
      img: booking,
      color: theme.colors.purple,
      href: "/bookings?tabStatus=completed",
    },
    {
      count: paginationData?.total || 0,
      key: "Total Referrals",
      img: user,
      color: theme.colors.cyan,
      href: "/referrals",
    },
    {
      count: metricData?.bookingData?.ads_info?.totalAds || 0,
      key: "Total Ads",
      img: ads,
      color: theme.colors.purple,
      href: "/ads-management",
    },
  ];
  return (
    <DashboardLayout pageTitle="Dashboard">
      {/* <DashboardCharts /> */}
      <DashboardContainer>
        <ToastContainer />
        <div className="metrics__cards">
          <Flex wrap="wrap" gap="1.5rem">
            {metrics.map((metric) => {
              if (
                authUser?.role?.includes("occupationalManager") &&
                !occupationalManagerCard.includes(metric.key)
              )
                return null;
              return (
                <MetricsCard
                  key={metric.key}
                  metric={metric}
                  href={metric.href}
                />
              );
            })}
          </Flex>
        </div>
        {isLoadingRecent ? (
          <div className="loader-container">
            <ScaleLoader color="#7E00C4" height={50} width={8} />
          </div>
        ) : (
          !authUser?.role?.includes("occupationalManager") && (
            <RecentBookingsTable
              rows={recentBookings}
              pgn={bPageNumber}
              setPgn={setBPageNumber}
              paginationData={bPaginationData}
            />
          )
        )}
        <ReferralTable
          pgn={pageNumber}
          setpgn={setPageNumber}
          rows={allReferrals}
          paginationData={paginationData}
        />
      </DashboardContainer>
    </DashboardLayout>
  );
};
export default Dashboard;
